import { extendedProducts } from "../client/src/lib/extended-products";
import { performanceRecords } from "../client/src/lib/performance-records";
import { applications, products, technicalArticles } from "../client/src/lib/site-data";
import { specificProducts } from "../client/src/lib/specific-products";
import { productBrowsePresets } from "../shared/productBrowsePaths";

export const SITE_ORIGIN = "https://duratarp.cn";

export type PublicRouteGroup = "core" | "catalogue" | "materials" | "applications" | "performance" | "technical" | "company";

export type PublicContentRoute = {
  path: string;
  title: string;
  description: string;
  group: PublicRouteGroup;
};

const staticPublicRoutes: PublicContentRoute[] = [
  { path: "/", title: "PVC Tarpaulin Manufacturer | DuraTarp", description: "DuraTarp PVC tarpaulin and coated-fabric systems overview.", group: "core" },
  { path: "/products", title: "Products", description: "Directory of exact fixed-SKU PVC tarpaulin records.", group: "catalogue" },
  { path: "/materials", title: "Materials", description: "PVC coated, laminated, mesh and membrane material routes.", group: "materials" },
  { path: "/material-routes", title: "Product Finder", description: "Application-led PVC material selection route.", group: "core" },
  { path: "/applications", title: "Applications", description: "PVC tarpaulin application selection pages.", group: "applications" },
  { path: "/performance", title: "Performance", description: "Performance-led PVC tarpaulin material discussions.", group: "performance" },
  { path: "/specifications", title: "Specifications", description: "PVC tarpaulin specification selection framework.", group: "technical" },
  { path: "/custom-pvc-tarpaulin", title: "Custom PVC Tarpaulin", description: "Custom PVC tarpaulin solution route.", group: "core" },
  { path: "/factory", title: "Factory", description: "DuraTarp manufacturing process overview.", group: "company" },
  { path: "/quality", title: "Quality", description: "DuraTarp quality-control overview.", group: "company" },
  { path: "/manufacturer", title: "Manufacturer", description: "DuraTarp manufacturer overview.", group: "company" },
  { path: "/technical-center", title: "Technical Center", description: "PVC tarpaulin buyer guides and technical content.", group: "technical" },
  { path: "/resources", title: "Resources", description: "PVC tarpaulin resources for buyers.", group: "technical" },
  { path: "/about-us", title: "About DuraTarp", description: "DuraTarp company overview.", group: "company" },
  { path: "/certifications", title: "Certifications", description: "DuraTarp certification information.", group: "company" },
  { path: "/case-studies", title: "Case Studies", description: "DuraTarp project case studies.", group: "company" },
  { path: "/contact", title: "Contact", description: "Contact DuraTarp for a material discussion.", group: "company" },
];

const absoluteUrl = (path: string) => `${SITE_ORIGIN}${path}`;

export function buildPublicContentManifest(): PublicContentRoute[] {
  const routes: PublicContentRoute[] = [
    ...staticPublicRoutes,
    ...productBrowsePresets.map((preset) => ({ path: `/products/${preset.slug}`, title: preset.title, description: preset.description, group: "catalogue" as const })),
    ...products.map((product) => ({ path: `/${product.slug}`, title: product.name, description: product.description, group: "core" as const })),
    ...specificProducts.map((product) => ({ path: `/products/${product.slug}`, title: product.name, description: product.summary, group: "catalogue" as const })),
    ...extendedProducts.map((product) => ({ path: `/materials/${product.slug}`, title: product.name, description: product.brief, group: "materials" as const })),
    ...applications.map((application) => ({ path: `/applications/${application.slug}`, title: application.title, description: application.text, group: "applications" as const })),
    ...performanceRecords.map((record) => ({ path: `/performance/${record.slug}`, title: record.title, description: record.intro, group: "performance" as const })),
    ...technicalArticles.map((article) => ({ path: `/technical-center/${article.slug}`, title: article.title, description: article.description, group: "technical" as const })),
  ];

  const paths = new Set<string>();
  return routes.filter((route) => {
    if (paths.has(route.path)) return false;
    paths.add(route.path);
    return true;
  });
}

const escapeXml = (value: string) => value.replace(/[<>&'\"]/g, (character) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", "'": "&apos;", '"': "&quot;" })[character] ?? character);

export function createSitemapXml(routes = buildPublicContentManifest()): string {
  const urlEntries = routes.map((route) => `  <url><loc>${escapeXml(absoluteUrl(route.path))}</loc></url>`).join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urlEntries}\n</urlset>\n`;
}

const asMarkdownLink = (route: PublicContentRoute) => `- [${route.title}](${absoluteUrl(route.path)}): ${route.description}`;

export function createLlmsTxt(routes = buildPublicContentManifest()): string {
  const coreRoutes = routes.filter((route) => route.group === "core" && route.path !== "/" && route.path !== "/custom-pvc-tarpaulin");
  const primaryRoutes = routes.filter((route) => ["/", "/products", "/material-routes", "/applications", "/materials", "/specifications", "/technical-center", "/contact"].includes(route.path));
  const applicationRoutes = routes.filter((route) => route.group === "applications" && route.path !== "/applications");
  const technicalRoutes = routes.filter((route) => route.group === "technical" && route.path.startsWith("/technical-center/"));

  return [
    "# DuraTarp — PVC Tarpaulin & Coated Fabric Systems",
    "",
    "> DuraTarp is a B2B PVC tarpaulin and coated-fabric website for industrial, transport, shelter, outdoor protection, membrane and inflatable-material projects. Use the linked product, application and technical pages to understand the relevant material route and request an order-specific discussion.",
    "",
    "This file is generated during every production build from DuraTarp's current public route and product data.",
    "",
    "## Start Here",
    "",
    ...primaryRoutes.map(asMarkdownLink),
    "",
    "## Core Material Routes",
    "",
    ...coreRoutes.map(asMarkdownLink),
    "",
    "## Product Catalogue",
    "",
    `- [Exact fixed-SKU catalogue](${absoluteUrl("/products")}): Browse ${specificProducts.length} current fixed SKU records by product family, buyer use, GSM, width and surface. Use each individual SKU page as the source of its listed specification.`,
    `- [Project material routes](${absoluteUrl("/materials")}): Browse ${extendedProducts.length} configurable or project-specification material routes. These routes support a discussion; they do not promise every construction or performance level.`,
    "",
    "## Application Guides",
    "",
    ...applicationRoutes.map(asMarkdownLink),
    "",
    "## Performance and Technical Guides",
    "",
    ...routes.filter((route) => route.group === "performance").map(asMarkdownLink),
    ...technicalRoutes.map(asMarkdownLink),
    "",
    "## Information Boundaries",
    "",
    "- Treat an exact fixed-SKU page as the source for its listed GSM, base fabric, thread density, width, roll length, surface and packaging details.",
    "- Do not infer regulatory, certification, waterproofing, flame-retardant, UV, strength, welding or durability claims from a product name alone. Confirm requirements against the exact material and order scope.",
    "- Standard packaging is Kraft Paper. For Technical Data Sheets, samples or final specifications, use the contact page to request factory sales support.",
    "",
    "## Crawling and Canonical Sources",
    "",
    `- [Sitemap](${absoluteUrl("/sitemap.xml")}): Automatically generated canonical list of public DuraTarp URLs.`,
    `- [Robots policy](${absoluteUrl("/robots.txt")}): Public crawl rules and sitemap location.`,
    `- [llms.txt](${absoluteUrl("/llms.txt")}): This automatically generated Markdown guide for language models and retrieval systems.`,
    "",
  ].join("\n");
}
